import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Box, Alert as MuiAlert,
  FormControl, InputLabel, Select, MenuItem,
  IconButton, Typography, Autocomplete,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { visitationAPI, medicineAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function VisitationDetails() {
  const { visitationId } = useParams();
  const navigate = useNavigate();
  const { isDoctor } = useAuth();
  const [visitation, setVisitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [medicines, setMedicines] = useState([]);

  useEffect(() => {
    fetchVisitation();
  }, [visitationId]);

  const fetchVisitation = async () => {
    try {
      setLoading(true);
      const response = await visitationAPI.getById(visitationId);
      setVisitation(response.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch visitation details');
    } finally {
      setLoading(false);
    }
  };

  const fetchMedicines = async () => {
    try {
      const res = await medicineAPI.getAll({ limit: 1000 });
      setMedicines(res.data.data || []);
    } catch { }
  };

  const openEdit = () => {
    setEditData({
      doctorName: visitation.doctorName || '',
      visitDate: dayjs(visitation.visitDate),
      chiefComplaint: visitation.chiefComplaint || '',
      diagnosis: visitation.diagnosis || '',
      prescription: visitation.prescription?.length > 0
        ? visitation.prescription.map(p => ({ ...p }))
        : [{ medicineName: '', dosage: '', frequency: 'BD', duration: '', instructions: '' }],
      treatment: visitation.treatment?.length > 0
        ? visitation.treatment.map(t => ({ ...t }))
        : [{ treatmentName: '', description: '', frequency: 'Monthly', sessions: '' }],
      notes: visitation.notes || '',
      followUpDate: visitation.followUpDate ? dayjs(visitation.followUpDate) : null,
    });
    setEditError('');
    setEditOpen(true);
    fetchMedicines();
  };

  const handleEditSave = async () => {
    if (!editData.doctorName.trim()) { setEditError('Doctor name is required'); return; }
    setEditLoading(true);
    setEditError('');
    try {
      await visitationAPI.update(visitationId, {
        ...editData,
        prescription: editData.prescription.filter(p => p.medicineName?.trim()),
        treatment: editData.treatment.filter(t => t.treatmentName?.trim()),
        visitDate: editData.visitDate?.toISOString(),
        followUpDate: editData.followUpDate?.toISOString() || null,
      });
      setEditOpen(false);
      fetchVisitation();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setEditLoading(false);
    }
  };

  const updatePrescription = (idx, field, value) => {
    const updated = [...editData.prescription];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditData({ ...editData, prescription: updated });
  };
  const addPrescriptionRow = () =>
    setEditData({ ...editData, prescription: [...editData.prescription, { medicineName: '', dosage: '', frequency: 'BD', duration: '', instructions: '' }] });
  const removePrescriptionRow = (idx) =>
    setEditData({ ...editData, prescription: editData.prescription.filter((_, i) => i !== idx) });

  const updateTreatment = (idx, field, value) => {
    const updated = [...editData.treatment];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditData({ ...editData, treatment: updated });
  };
  const addTreatmentRow = () =>
    setEditData({ ...editData, treatment: [...editData.treatment, { treatmentName: '', description: '', frequency: 'Monthly', sessions: '' }] });
  const removeTreatmentRow = (idx) =>
    setEditData({ ...editData, treatment: editData.treatment.filter((_, i) => i !== idx) });

  const handlePrint = () => {
    window.print();
  };

  const handlePrintInvoice = () => {
    const printWindow = window.open('', '_blank');

    const visitationFee = 700;
    const amountBeforeGST = visitationFee / 1.05;
    const gstAmount = visitationFee - amountBeforeGST;
    const cgst = gstAmount / 2;
    const sgst = gstAmount / 2;

    const patientAddress = visitation.patient ? [visitation.patient.street, visitation.patient.city, visitation.patient.state, visitation.patient.zipCode].filter(Boolean).join(', ') : '';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - Visitation ${visitation.id}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: A5; margin: 0; }
          body {
            font-family: 'Arial', sans-serif;
            width: 148mm;
            height: 210mm;
            padding: 8mm;
            margin: 0 auto;
            font-size: 9pt;
          }
          .bill {
            border: 1px solid #000;
            padding: 6mm;
            height: 100%;
            display: flex;
            flex-direction: column;
          }
          .header {
            border-bottom: 1px dashed #000;
            padding-bottom: 4mm;
            margin-bottom: 4mm;
          }
          .header-top {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 3mm;
          }
          .header-logo {
            width: 25mm;
            height: auto;
            object-fit: contain;
          }
          .header-center {
            flex: 1;
            text-align: center;
            padding: 0 3mm;
          }
          .header-center h1 {
            font-size: 13pt;
            margin-bottom: 1mm;
            font-weight: bold;
            line-height: 1.2;
          }
          .header-center h2 {
            font-size: 9pt;
            margin-bottom: 2mm;
            font-weight: 600;
            line-height: 1.2;
          }
          .header-center .address {
            font-size: 7pt;
            line-height: 1.3;
            margin: 0;
          }
          .bill-details {
            text-align: center;
            margin-top: 2mm;
          }
          .bill-details p {
            font-size: 8pt;
            margin: 0.5mm 0;
            line-height: 1.2;
          }
          .section { margin: 3mm 0; }
          .section-title {
            font-weight: bold;
            font-size: 9pt;
            border-bottom: 1px solid #000;
            margin-bottom: 2mm;
            padding-bottom: 1mm;
          }
          .row {
            display: flex;
            justify-content: space-between;
            margin: 1.5mm 0;
            font-size: 8pt;
            line-height: 1.3;
          }
          .info-row { justify-content: flex-start; gap: 2mm; }
          .info-row span:first-child { font-weight: bold; }
          .patient-info-container { display: flex; justify-content: space-between; align-items: flex-start; }
          .patient-info-right { text-align: left; max-width: 50%; font-size: 8pt; line-height: 1.3; }
          .items-table {
            width: 100%;
            margin: 3mm 0;
            border-collapse: collapse;
          }
          .items-table th {
            text-align: left;
            border-bottom: 1px solid #000;
            padding: 2mm 0;
            font-size: 8pt;
            font-weight: bold;
          }
          .items-table td {
            padding: 1.5mm 0;
            font-size: 8pt;
            line-height: 1.2;
          }
          .items-table .item-name { width: 50%; }
          .items-table .item-qty { width: 10%; text-align: center; }
          .items-table .item-price { width: 15%; text-align: right; }
          .items-table .item-gst { width: 10%; text-align: right; font-size: 7pt; }
          .items-table .item-total { width: 15%; text-align: right; }
          .totals {
            border-top: 1px dashed #000;
            padding-top: 3mm;
            margin-top: 3mm;
          }
          .totals .row { font-size: 9pt; margin: 1.5mm 0; }
          .totals .final-total {
            font-size: 11pt;
            font-weight: bold;
            border-top: 1px solid #000;
            padding-top: 2mm;
            margin-top: 2mm;
          }
          .footer {
            text-align: center;
            border-top: 1px dashed #000;
            padding-top: 3mm;
            margin-top: auto;
            font-size: 7pt;
            line-height: 1.3;
          }
          @media print {
            body {
              padding: 8mm;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print { display: none; }
          }
          @media screen {
            body {
              background: #f0f0f0;
              padding: 10mm;
            }
          }
        </style>
      </head>
      <body>
        <div class="bill">
          <div class="header">
            <div class="header-top">
              <img src="/logo.jpg" alt="Logo" class="header-logo" />
              <div class="header-center">
                <h1>GLOW with SuperDoc</h1>
                <h2>MSG Hotels & Resorts</h2>
                <p class="address">Opp. MSG International School</p>
                <p class="address">Nezia Khera, Sirsa</p>
                <p class="address">Haryana, India</p>
                <p class="address">Pin Code 125055</p>
                <p class="address">GST NO: 06AAJCA4867D4ZL</p>
              </div>
              <img src="/logo2.png" alt="Logo" class="header-logo" />
            </div>
            <div class="bill-details">
              <p><strong>Consultation Invoice</strong></p>
              <p>Invoice No: VIS-${visitation.id}</p>
              <p>Date: ${new Date(visitation.visitDate).toLocaleDateString()} ${new Date(visitation.createdAt || visitation.visitDate || new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</p>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Patient Details</div>
            <div class="patient-info-container">
              <div>
                <div class="row info-row"><span>Name:</span><span>${visitation.patient?.firstName} ${visitation.patient?.lastName}</span></div>
                <div class="row info-row"><span>Phone:</span><span>${visitation.patient?.phone}</span></div>
                <div class="row info-row"><span>Age/Gender:</span><span>${calculateAge(visitation.patient?.dateOfBirth)} years / ${visitation.patient?.gender}</span></div>
              </div>
              ${patientAddress ? `<div class="patient-info-right"><strong>Address:</strong> ${patientAddress}</div>` : ''}
            </div>
          </div>

          <div class="section">
            <div class="section-title">Consultation Details</div>
            <table class="items-table">
              <thead>
                <tr>
                  <th class="item-name">Description</th>
                  <th class="item-qty">Qty</th>
                  <th class="item-price">Rate</th>
                  <th class="item-gst">GST (5%)</th>
                  <th class="item-total">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="item-name">Consultation by Dr. ${visitation.doctorName}</td>
                  <td class="item-qty">1</td>
                  <td class="item-price">₹${Math.round(amountBeforeGST).toLocaleString('en-IN')}</td>
                  <td class="item-gst">₹${Math.round(gstAmount).toLocaleString('en-IN')}</td>
                  <td class="item-total">₹${Math.round(visitationFee).toLocaleString('en-IN')}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="totals">
            <div class="row"><span>Subtotal (Before GST):</span><span>₹${Math.round(amountBeforeGST).toLocaleString('en-IN')}</span></div>
            <div class="row"><span>CGST (2.5%):</span><span>₹${Math.round(cgst).toLocaleString('en-IN')}</span></div>
            <div class="row"><span>SGST (2.5%):</span><span>₹${Math.round(sgst).toLocaleString('en-IN')}</span></div>
            <div class="row"><span>Total GST (5%):</span><span>₹${Math.round(gstAmount).toLocaleString('en-IN')}</span></div>
            <div class="row final-total"><span>Total Amount:</span><span>₹${Math.round(visitationFee).toLocaleString('en-IN')}</span></div>
          </div>

          <div class="footer">
            <p>Thank you for visiting!</p>
            <p>Please keep this invoice for your records</p>
          </div>
        </div>

        <div class="no-print" style="text-align: center; margin-top: 20px;">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">Print Invoice</button>
          <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; margin-left: 10px;">Close</button>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const isSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  };

  const hasFirstSessionOnVisitDate = (treatmentName) => {
    if (!visitation.scheduledTreatments) return false;
    const treatment = visitation.scheduledTreatments.find(
      t => t.treatmentType === treatmentName
    );
    return treatment && treatment.hasFirstSession &&
      isSameDay(treatment.firstSessionDate, visitation.visitDate);
  };

  const handleScheduleTreatment = (treatmentName) => {
    const encodedTreatmentName = encodeURIComponent(treatmentName);
    navigate(`/treatment/new/${visitation.patientId}?treatmentName=${encodedTreatmentName}`);
  };

  if (loading) {
    return <div className="loading">Loading visitation details...</div>;
  }

  if (error || !visitation) {
    return <div className="error-message">❌ {error || 'Visitation not found'}</div>;
  }

  return (
    <div>
      {/* Screen View */}
      <div className="card no-print">
        <h2>📋 Visitation Details</h2>

        <div className="btn-group">
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Print Prescription
          </button>
          <button className="btn btn-success" onClick={handlePrintInvoice}>
            💰 Print Invoice (₹700)
          </button>
          {isDoctor() && (
            <button className="btn btn-warning" onClick={openEdit}>
              ✏️ Edit Visitation
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/patient/${visitation.patientId}`)}
          >
            ← Back to Patient
          </button>
        </div>
      </div>

      {/* Printable View */}
      <div className="prescription-print" style={{ marginTop: '29%' }}>
        <table style={{ width: '100%', marginBottom: '25px' }}>
          <tbody>
            <tr>
              <td><div><strong>Patient:</strong> {visitation.patient?.firstName} {visitation.patient?.lastName}</div></td>
              <td style={{ textAlign: 'right' }}><div><strong>Date:</strong> {formatDate(visitation.visitDate)}</div></td>
            </tr>
            <tr>
              <td>
                <div style={{ marginTop: '8px' }}><strong>Age / Gender:</strong> {calculateAge(visitation.patient?.dateOfBirth)} years / {visitation.patient?.gender}</div>
              </td>
            </tr>
          </tbody>
        </table>
        {/* <p><strong>Name:</strong> {visitation.patient?.firstName} {visitation.patient?.lastName}</p>
          <p><strong>Age/Gender:</strong> {calculateAge(visitation.patient?.dateOfBirth)} years / {visitation.patient?.gender}</p> */}
        {/* <p><strong>Phone:</strong> {visitation.patient?.phone}</p> */}
        {/* <p><strong>Doctor:</strong> Dr. {visitation.doctorName}</p> */}
        {/* <p><strong>Date:</strong> {formatDateTime(visitation.visitDate)}</p> */}
        {/* {visitation.patient?.allergies && (
            <p><strong>Allergies:</strong> <span style={{color: '#f44336', fontWeight: 'bold'}}>{visitation.patient.allergies}</span></p>
          )} */}
        {/* </div> */}

        {visitation.chiefComplaint && (
          <p><h3><strong>Chief Complaint:</strong></h3> {visitation.chiefComplaint}</p>
        )}
        {visitation.diagnosis && (
          <p><h3><strong>Diagnosis:</strong></h3> {visitation.diagnosis}</p>
        )}

        {visitation.treatment && visitation.treatment.length > 0 && (
          <div className="prescription-medicines">
            <h3><strong>Treatment Plan</strong></h3>
            {visitation.treatment.map((treat, index) => (
              <div key={index}>
                <p style={{ marginBottom: '0.25rem', lineHeight: '1.6' }}>
                  {index + 1}. {treat.treatmentName} {treat.frequency} {treat.sessions + ' sessions' || '-'}  {treat.description ? `- ${treat.description}` : ''}
                </p>
                {hasFirstSessionOnVisitDate(treat.treatmentName) && (
                  <p style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                    1st Sitting Done.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {visitation.prescription && visitation.prescription.length > 0 && (
          <div className="prescription-medicines">
            <h3><strong>Prescribed Medicines</strong></h3>
            {visitation.prescription.map((med, index) => (
              <p key={index} style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
                {index + 1}. {med.medicineName} {med.dosage} {med.frequency} {med.duration} {med.instructions ? `- ${med.instructions}` : ''}
              </p>
            ))}
          </div>
        )}

        {visitation.notes && (
          <div style={{ marginTop: '2rem' }}>
            <h3><strong>Instructions:</strong></h3>
            <p>{visitation.notes}</p>
          </div>
        )}

        {visitation.followUpDate && (
          <div style={{ marginTop: '2rem' }}>
            <h3><strong>Follow-up</strong></h3>
            <p><strong>Next Appointment:</strong> {formatDate(visitation.followUpDate)}</p>
          </div>
        )}

        <div className="prescription-footer">
          <p>_________________________</p>
          <p>Dr. {visitation.doctorName}</p>
          <p>Signature & Date</p>
        </div>
      </div>

      {/* Screen View Content */}
      <div className="card no-print">
        <div className="info-box">
          <h3>👤 Patient Information</h3>
          <p><strong>Name:</strong> {visitation.patient?.firstName} {visitation.patient?.lastName}</p>
          <p><strong>Age/Gender:</strong> {calculateAge(visitation.patient?.dateOfBirth)} years / {visitation.patient?.gender}</p>
          <p><strong>Phone:</strong> {visitation.patient?.phone}</p>
          {visitation.patient?.allergies && (
            <p><strong>⚠️ Allergies:</strong> <span style={{ color: 'var(--error-color)', fontWeight: 'bold' }}>{visitation.patient.allergies}</span></p>
          )}
        </div>

        <h3>📝 Visit Details</h3>
        <p><strong>Doctor:</strong> {visitation.doctorName}</p>
        <p><strong>Visit Date:</strong> {formatDateTime(visitation.visitDate)}</p>
        {visitation.chiefComplaint && <p><strong>Chief Complaint:</strong> {visitation.chiefComplaint}</p>}
        {visitation.diagnosis && <p><strong>Diagnosis:</strong> {visitation.diagnosis}</p>}

        {visitation.prescription && visitation.prescription.length > 0 && (
          <>
            <h3 style={{ marginTop: '2rem' }}>💊 Prescription</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Medicine Name</th>
                    <th>Dosage</th>
                    <th>Frequency</th>
                    <th>Duration</th>
                    <th>Instructions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitation.prescription.map((med, index) => (
                    <tr key={index}>
                      <td><strong>{med.medicineName}</strong></td>
                      <td>{med.dosage}</td>
                      <td><span className="badge badge-info">{med.frequency}</span></td>
                      <td>{med.duration}</td>
                      <td>{med.instructions || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {visitation.treatment && visitation.treatment.length > 0 && (
          <>
            <h3 style={{ marginTop: '2rem' }}>🩺 Treatment Plan</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Treatment Name</th>
                    <th>Frequency</th>
                    <th>Sessions</th>
                    <th>Description</th>
                    {isDoctor() && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {visitation.treatment.map((treat, index) => (
                    <tr key={index}>
                      <td><strong>{treat.treatmentName}</strong></td>
                      <td><span className="badge badge-success">{treat.frequency}</span></td>
                      <td>{treat.sessions + ' sessions' || '-'}</td>
                      <td>{treat.description || '-'}</td>
                      {isDoctor() && (
                        <td>
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                            onClick={() => handleScheduleTreatment(treat.treatmentName)}
                            title="Schedule this treatment"
                          >
                            📅 Schedule
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {visitation.notes && (
          <>
            <h3 style={{ marginTop: '2rem' }}>📝 Instructions</h3>
            <p>{visitation.notes}</p>
          </>
        )}

        {visitation.followUpDate && (
          <>
            <h3 style={{ marginTop: '2rem' }}>📅 Follow-up</h3>
            <p><strong>Next Appointment:</strong> {formatDate(visitation.followUpDate)}</p>
          </>
        )}
      </div>

      {/* ── Edit Visitation Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Visitation</DialogTitle>
        <DialogContent dividers>
          {editError && <MuiAlert severity="error" sx={{ mb: 2 }}>{editError}</MuiAlert>}
          {editData && (
            <Box>
              {/* Visit Details */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Visit Details</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Doctor Name *"
                    value={editData.doctorName}
                    onChange={e => setEditData({ ...editData, doctorName: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DateTimePicker label="Visit Date & Time *" value={editData.visitDate}
                    onChange={v => setEditData({ ...editData, visitDate: v })} sx={{ width: '100%' }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Chief Complaint" multiline rows={2}
                    value={editData.chiefComplaint}
                    onChange={e => setEditData({ ...editData, chiefComplaint: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Diagnosis" multiline rows={2}
                    value={editData.diagnosis}
                    onChange={e => setEditData({ ...editData, diagnosis: e.target.value })} />
                </Grid>
              </Grid>

              {/* Prescription */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>Prescription</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addPrescriptionRow}>Add Medicine</Button>
              </Box>
              {editData.prescription.map((p, i) => (
                <Grid container spacing={1} sx={{ mb: 1 }} key={i} alignItems="center">
                  <Grid item xs={3}>
                    <Autocomplete freeSolo size="small"
                      options={medicines.map(m => m.name)}
                      value={p.medicineName}
                      onChange={(_, v) => updatePrescription(i, 'medicineName', v || '')}
                      onInputChange={(_, v) => updatePrescription(i, 'medicineName', v)}
                      renderInput={(params) => <TextField {...params} label="Medicine" />} />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField fullWidth size="small" label="Dosage" value={p.dosage}
                      onChange={e => updatePrescription(i, 'dosage', e.target.value)} />
                  </Grid>
                  <Grid item xs={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Freq</InputLabel>
                      <Select value={p.frequency || 'BD'} label="Freq"
                        onChange={e => updatePrescription(i, 'frequency', e.target.value)}>
                        {['OD', 'BD', 'TDS', 'QID'].map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField fullWidth size="small" label="Duration" value={p.duration}
                      onChange={e => updatePrescription(i, 'duration', e.target.value)} />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField fullWidth size="small" label="Instructions" value={p.instructions}
                      onChange={e => updatePrescription(i, 'instructions', e.target.value)} />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton size="small" color="error" onClick={() => removePrescriptionRow(i)}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              {/* Treatment Plan */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>Treatment Plan</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addTreatmentRow}>Add Treatment</Button>
              </Box>
              {editData.treatment.map((t, i) => (
                <Grid container spacing={1} sx={{ mb: 1 }} key={i} alignItems="center">
                  <Grid item xs={3}>
                    <TextField fullWidth size="small" label="Treatment Name" value={t.treatmentName}
                      onChange={e => updateTreatment(i, 'treatmentName', e.target.value)} />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Frequency</InputLabel>
                      <Select value={t.frequency || 'Monthly'} label="Frequency"
                        onChange={e => updateTreatment(i, 'frequency', e.target.value)}>
                        {['Once', 'Daily', 'Twice Daily', 'Weekly', 'Bi-Weekly', 'Monthly', 'Every 45 Days', 'Quarterly', 'Yearly', 'As Needed']
                          .map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField fullWidth size="small" label="Sessions" value={t.sessions}
                      onChange={e => updateTreatment(i, 'sessions', e.target.value)} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField fullWidth size="small" label="Description" value={t.description}
                      onChange={e => updateTreatment(i, 'description', e.target.value)} />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton size="small" color="error" onClick={() => removeTreatmentRow(i)}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              {/* Notes & Follow-up */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Instructions / Notes" multiline rows={2}
                    value={editData.notes}
                    onChange={e => setEditData({ ...editData, notes: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker label="Follow-up Date" value={editData.followUpDate}
                    onChange={v => setEditData({ ...editData, followUpDate: v })} sx={{ width: '100%' }} />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editLoading}>
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default VisitationDetails;
